import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMCPQueryScope, type MCPQueryContext } from "../hooks/useMCPQueryScope";
import { useMCPTools } from "../hooks/useMCPTools";
import { mcpQueryKeys } from "../query-keys";
import { getKeycloakUserInfo } from "@/services/keycloak";
import { getActiveOrganizationId } from "@/utils/requestScope";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Spinner } from "../../../components/ui/spinner";
import AccessLevelTabs from "./AccessLevelTabs";
import FormField from "./FormField";
import ProjectSelect from "./ProjectSelect";
import UserMultiSelect from "./UserMultiSelect";
import { TOOL_ACCESS_LEVELS } from "../constants";
import { normalizeSharingUsers } from "../adapters";
import { useUpdateToolSharing } from "../hooks/useUpdateToolSharing";
import { useMCPProjectTarget } from "../hooks/useMCPProjectTarget";
import { useUsers } from "../hooks/useUsers";
import type {
  MCPConnectionTool,
  MCPSharingUserItem,
  MCPToolSharing,
} from "../../../services/agent-tools-service";
import type { ToolAccessLevel } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  tool: MCPConnectionTool | null;
  canManage: boolean;
  getFocusFallback?: (toolId: string) => HTMLElement[];
};

// Map the tool's stored sharing scope onto the 3 tabs (organization collapses
// into "inherit" since tools only expose inherit/project/user).
const toAccessLevel = (
  sharing: MCPToolSharing | undefined,
): ToolAccessLevel => {
  if (sharing?.scope === "project") return "project";
  if (sharing?.scope === "user") return "user";
  return "inherit";
};

type Session = {
  scope: MCPQueryContext;
  connectionId: string;
  tool: MCPConnectionTool;
  trigger: HTMLElement | null;
};

const liveScopeMatches = (scope: MCPQueryContext) => scope.enabled && scope.active &&
  !scope.controller.signal.aborted && getKeycloakUserInfo()?.id === scope.key.principalId &&
  getActiveOrganizationId() === scope.requestScope.organizationId;

const ToolAccessSession = ({
  onOpenChange,
  connectionId,
  tool,
  canManage,
  session,
  isLatestOpening,
  getFocusFallback,
}: Omit<Props, "open" | "tool"> & { tool: MCPConnectionTool; session: Session; isLatestOpening: () => boolean }) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const [level, setLevel] = useState<ToolAccessLevel>(() => toAccessLevel(tool.sharing));
  const usersQuery = useUsers({ enabled: level === "user" });
  const target = useMCPProjectTarget(true);
  const { projectId, selectProject: setProjectId, seedProject } = target;
  const [users, setUsers] = useState<MCPSharingUserItem[]>(() => tool.sharing?.scope === "user" ? normalizeSharingUsers(tool.sharing.items) : []);
  const alive = useRef(false);
  const inFlight = useRef(false);
  const restoreFocus = useRef(false);
  const client = useQueryClient();
  // Observe the same Stage 1 query (no new cache); fetching/staleness changes
  // must rerender the Save state even when cached rows retain their identity.
  useMCPTools(connectionId);
  const isSessionCurrent = useCallback(() => alive.current && liveScopeMatches(session.scope), [session]);

  const mutation = useUpdateToolSharing({
    isSessionCurrent,
    onSuccess: () => {
      if (!isSessionCurrent()) return;
      restoreFocus.current = true;
      onOpenChange(false);
    },
  });

  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      // Authoritative removal closes through the owner, not onOpenChange.
      // Record that reason during layout cleanup, before Radix's deferred
      // close autofocus. Errors, invalidation and in-flight data are not proof
      // of removal. The callback still checks live scope/latest-opening ownership.
      const state = client.getQueryState<MCPConnectionTool[]>(mcpQueryKeys.tools(session.scope.key, session.connectionId));
      if (state?.status === "success" && state.fetchStatus === "idle" && !state.isInvalidated &&
        Array.isArray(state.data) && !state.data.some((item) => item.id === session.tool.id)) {
        restoreFocus.current = true;
      }
    };
  }, [client, session]);

  // The opening snapshot is immutable for this session. Refetches must not
  // replace a user's draft with a newly allocated tool/sharing object.
  useEffect(() => {
    seedProject(tool.sharing?.scope === "project" ? tool.sharing.item : "");
  }, [tool, seedProject]);

  const hasCurrentTool = () => {
    const state = client.getQueryState<MCPConnectionTool[]>(mcpQueryKeys.tools(session.scope.key, connectionId));
    return state?.status === "success" && state.fetchStatus === "idle" && !state.isInvalidated &&
      Array.isArray(state.data) && state.data.some((item) => item.id === tool.id);
  };

  const isValid =
    level === "inherit" ||
    (level === "project" && target.isEligible) ||
    (level === "user" && users.length > 0);

  const handleChangeLevel = (next: ToolAccessLevel) => {
    setLevel(next);
    if (next !== "project") setProjectId("");
    if (next !== "user") setUsers([]);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManage || !isValid || mutation.isPending || inFlight.current || !isSessionCurrent() || !hasCurrentTool()) return;
    if (level === "project" && !target.validateProject()) return;
    if (level === "user" && !usersQuery.validateData()) return;

    const sharing: MCPToolSharing =
      level === "project"
        ? { scope: "project", item: projectId }
        : level === "user"
          ? { scope: "user", items: users }
          : { scope: "inherit" };

    inFlight.current = true;
    mutation.mutate({ connectionId, toolId: tool.id, sharing }, {
      onSettled: () => { inFlight.current = false; },
    });
  };

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if ((mutation.isPending || inFlight.current) && !next) return;
      if (!next) restoreFocus.current = true;
      onOpenChange(next);
    },
    [mutation.isPending, onOpenChange],
  );

  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    if (!restoreFocus.current || !isLatestOpening() || !liveScopeMatches(session.scope) ||
      document.querySelector('[role="dialog"][data-state="open"]')) return;
    const candidates = [session.trigger, ...(getFocusFallback?.(tool.id) ?? [])];
    for (const candidate of candidates) {
      if (!candidate?.isConnected || candidate === document.body ||
        candidate.matches(":disabled, [aria-disabled='true']") ||
        candidate.closest("[inert], [hidden], [aria-hidden='true']")) continue;
      const style = getComputedStyle(candidate);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") continue;
      candidate.focus();
      if (document.activeElement === candidate) break;
    }
  }, [session, isLatestOpening, getFocusFallback, tool.id]);

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        dir={direction}
        onCloseAutoFocus={handleCloseAutoFocus}
        onEscapeKeyDown={(event) => { if (inFlight.current || mutation.isPending) event.preventDefault(); }}
        onInteractOutside={(event) => { if (inFlight.current || mutation.isPending) event.preventDefault(); }}
        className="flex h-[432px] max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-[10px] border-border bg-card p-0 shadow-lg sm:max-w-[640px]"
      >
        <DialogHeader className="h-14 shrink-0 justify-center px-4 py-0 pe-12 text-start">
          <DialogTitle
            className="truncate text-xl leading-6 font-semibold tracking-[-0.02em]"
            title={t("mcpPage.toolAccess.title", { name: tool?.name })}
          >
            {t("mcpPage.toolAccess.title", { name: tool?.name })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("mcpPage.toolAccess.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div
            data-testid="tool-access-dialog-body"
            className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4"
          >
            <div
              className={`grid gap-4 ${
                level === "inherit"
                  ? "grid-cols-1"
                  : "grid-cols-1 sm:grid-cols-2"
              }`}
            >
              <FormField label={t("mcpPage.fields.accessLevel")} compact>
                <AccessLevelTabs
                  value={level}
                  onChange={handleChangeLevel}
                  options={TOOL_ACCESS_LEVELS}
                  compact
                />
              </FormField>

              {level === "project" && (
                <FormField label={t("mcpPage.fields.project")} compact>
                  <ProjectSelect
                    value={projectId}
                    onChange={setProjectId}
                    triggerClassName="h-9"
                  />
                </FormField>
              )}

              {level === "user" && (
                <FormField label={t("mcpPage.fields.users")} compact>
                  <UserMultiSelect
                    value={users}
                    onChange={setUsers}
                    isFocusOwnerCurrent={isSessionCurrent}
                    triggerClassName="h-9"
                  />
                </FormField>
              )}
            </div>
          </div>

          <DialogFooter className="h-[68px] shrink-0 flex-row items-center justify-end gap-2 px-4 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t("mcpPage.actions.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!canManage || !isValid || mutation.isPending || !hasCurrentTool() || (level === "user" && !usersQuery.canUseData)}
              aria-busy={mutation.isPending}
            >
              {mutation.isPending && <Spinner className="size-4" />}
              {mutation.isPending
                ? t("mcpPage.actions.saving")
                : t("mcpPage.toolAccess.submitIdle")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ToolAccessDialog = (props: Props) => {
  const scope = useMCPQueryScope();
  const capture = (): Session | null => props.open && props.tool ? {
    scope, connectionId: props.connectionId, tool: props.tool,
    trigger: document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null,
  } : null;
  const [opening, setOpening] = useState(() => ({ open: props.open, session: capture() }));
  // Reconcile only the controlled open edge, before committing any handlers.
  // Identity changes while open invalidate rather than rebind an old draft.
  if (opening.open !== props.open) setOpening({ open: props.open, session: capture() });
  const session = opening.session;
  const ownsSession = session && session.scope === scope && session.connectionId === props.connectionId && session.tool.id === props.tool?.id;
  const focusOwner = useRef(session);
  useLayoutEffect(() => {
    if (props.open) focusOwner.current = ownsSession ? session : null;
  }, [props.open, ownsSession, session]);
  if (!props.open || !session || !ownsSession) return null;
  return <ToolAccessSession {...props} tool={session.tool} session={session} isLatestOpening={() => focusOwner.current === session} />;
};

export default ToolAccessDialog;
